import React from "react";
import { IQWatchDeviceWithData } from "../../types";
import { DeviceFilter } from "../../store/dashboardSlice";
import { Battery, Wifi, WifiOff, AlertTriangle } from "lucide-react";

interface IQWatchSummaryCardsProps {
  devices: IQWatchDeviceWithData[];
  currentFilter?: DeviceFilter;
  onFilterChange?: (filter: DeviceFilter) => void;
  summary?: {
    total: number;
    online: number;
    offline: number;
    low_battery: number;
  } | null;
  pagination?: {
    total: number;
  } | null;
}

const IQWatchSummaryCards: React.FC<IQWatchSummaryCardsProps> = ({
  devices,
  currentFilter = "all",
  onFilterChange,
  summary,
  pagination,
}) => {
  // Use summary statistics from API if available, otherwise fall back to calculating from current page
  const allDevices = summary?.total ?? pagination?.total ?? devices.length;
  const onlineDevices =
    summary?.online ??
    devices.filter(
      (device) =>
        device.latest_data?.status === "running" ||
        device.device.status === "running"
    ).length;
  const offlineDevices = summary?.offline ?? allDevices - onlineDevices;
  const lowBatteryDevices =
    summary?.low_battery ??
    devices.filter((device) => {
      const soc = parseFloat(
        device.latest_data?.soc?.toString() || device.device.last_soc || "0"
      );
      return !isNaN(soc) && soc < 50;
    }).length;

  const handleCardClick = (filter: DeviceFilter) => {
    if (onFilterChange) {
      onFilterChange(filter);
    }
  };

  const getCardStyle = (filter: DeviceFilter) => {
    const isActive = currentFilter === filter;
    const baseStyle = "rounded-lg border p-6 transition-all duration-200";
    const hoverStyle = onFilterChange
      ? "hover:shadow-lg hover:-translate-y-0.5 cursor-pointer"
      : "";

    let borderStyle = "border-gray-200";
    let shadowStyle = "";
    let backgroundStyle = "bg-white";

    if (isActive) {
      // Active state: subtle colored border, shadow, and background based on filter type
      switch (filter) {
        case "all":
          borderStyle = "border-blue-300";
          shadowStyle = "shadow-md shadow-blue-100";
          backgroundStyle = "bg-blue-50";
          break;
        case "online":
          borderStyle = "border-green-300";
          shadowStyle = "shadow-md shadow-green-100";
          backgroundStyle = "bg-green-50";
          break;
        case "offline":
          borderStyle = "border-red-300";
          shadowStyle = "shadow-md shadow-red-100";
          backgroundStyle = "bg-red-50";
          break;
        case "low_battery":
          borderStyle = "border-orange-300";
          shadowStyle = "shadow-md shadow-orange-100";
          backgroundStyle = "bg-orange-50";
          break;
      }
    }

    return `${baseStyle} ${backgroundStyle} ${borderStyle} ${shadowStyle} ${hoverStyle}`;
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
      <div
        className={getCardStyle("all")}
        onClick={() => handleCardClick("all")}
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">All Devices</p>
            <p className="text-2xl font-bold text-gray-900">{allDevices}</p>
          </div>
          <div className="p-3 bg-blue-100 rounded-full">
            <Battery className="w-6 h-6 text-blue-600" />
          </div>
        </div>
        <div className="mt-4 text-sm text-gray-600">Total devices</div>
      </div>

      <div
        className={getCardStyle("online")}
        onClick={() => handleCardClick("online")}
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">Online Devices</p>
            <p className="text-2xl font-bold text-green-600">{onlineDevices}</p>
          </div>
          <div className="p-3 bg-green-100 rounded-full">
            <Wifi className="w-6 h-6 text-green-600" />
          </div>
        </div>
        <div className="mt-4 text-sm text-green-600">Running and connected</div>
      </div>

      <div
        className={getCardStyle("offline")}
        onClick={() => handleCardClick("offline")}
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">Offline Devices</p>
            <p className="text-2xl font-bold text-red-600">{offlineDevices}</p>
          </div>
          <div className="p-3 bg-red-100 rounded-full">
            <WifiOff className="w-6 h-6 text-red-600" />
          </div>
        </div>
        <div className="mt-4 text-sm text-red-600">Stopped or disconnected</div>
      </div>

      <div
        className={getCardStyle("low_battery")}
        onClick={() => handleCardClick("low_battery")}
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">
              Battery &lt; 30%
            </p>
            <p className="text-2xl font-bold text-orange-600">
              {lowBatteryDevices}
            </p>
          </div>
          <div className="p-3 bg-orange-100 rounded-full">
            <AlertTriangle className="w-6 h-6 text-orange-600" />
          </div>
        </div>
        <div className="mt-4 text-sm text-orange-600">Low battery warning</div>
      </div>
    </div>
  );
};

export default IQWatchSummaryCards;
